import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { AppointmentAPI } from "../../services/appointment.service";
import type { Appointment, AppointmentReminder, AppointmentSlot } from "../../services/appointment.service";
import { DoctorAPI } from "../../services/doctor.service";
import type { DoctorListItem } from "../../services/doctor.service";
import { PatientAPI } from "../../services/patient.service";
import { AppointmentStatusBadge } from "../../components/appointments/AppointmentStatusBadge";
import { RejectAppointmentModal } from "../../components/appointments/RejectAppointmentModal";
import { getSessionRole } from "../../components/RequireAuth";
import { useRole } from "../../hooks/useRole";
import Sidebar from "../../components/Sidebar";
import { AppHeader } from "../../components/layout/AppHeader";

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-IN", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

// ── Reminder badge (screen) ───────────────────────────────────────────────────
type ReminderStatus = "sent" | "delivered" | "opened" | "pending" | "failed";

const REMINDER_CONFIG: Record<ReminderStatus, { label: string; cls: string; dot: string }> = {
  delivered: { label: "Delivered", cls: "bg-green-50 text-green-700 border-green-200", dot: "bg-green-500" },
  sent:      { label: "Sent",      cls: "bg-cyan-50 text-cyan-700 border-cyan-200",    dot: "bg-cyan-500" },
  opened:    { label: "Opened",    cls: "bg-blue-50 text-blue-700 border-blue-200",    dot: "bg-blue-500" },
  pending:   { label: "Pending",   cls: "bg-slate-50 text-slate-500 border-slate-200", dot: "bg-slate-400" },
  failed:    { label: "Failed",    cls: "bg-red-50 text-red-600 border-red-200",       dot: "bg-red-500" },
};

const STATUS_COLORS: Record<ReminderStatus, string> = {
  delivered: "#15803d", sent: "#0e7490", opened: "#1d4ed8",
  pending: "#94a3b8", failed: "#dc2626",
};

const ReminderBadge: React.FC<{ status: ReminderStatus }> = ({ status }) => {
  const cfg = REMINDER_CONFIG[status];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full text-xs font-medium border px-2.5 py-1 ${cfg.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const AppointmentConfirmationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const printRef = useRef<HTMLDivElement>(null);

  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [slot, setSlot] = useState<AppointmentSlot | null>(null);
  const [doctors, setDoctors] = useState<DoctorListItem[]>([]);
  const [reminders, setReminders] = useState<AppointmentReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [patient, setPatient] = useState<any>(null);
  const [patientContact, setPatientContact] = useState<any>(null);
  const [downloading, setDownloading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [showReject, setShowReject] = useState(false);

  // Role says who may ever take these steps (approve/reject are also
  // role-gated server-side); the grants say whether this user's role currently
  // does. Approve / Reject / Resend / Complete change the appointment
  // (appointments.manage); Start Visit is a clinical write (visits.write).
  // This page itself only needs appointments.view.
  const userRole = getSessionRole();
  const { canOr } = useRole();
  const isClinicianRole = userRole === "Doctor" || userRole === "Admin";
  const canManageAppointments = canOr("appointments.manage", true);
  const canComplete = isClinicianRole && canManageAppointments;
  const canStartVisit = isClinicianRole && canOr("visits.write", true);
  const canApproveReject = isClinicianRole && canManageAppointments;

  useEffect(() => {
    DoctorAPI.list({ is_active: true }).then(setDoctors).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      AppointmentAPI.get(id),
      AppointmentAPI.getReminders(id).catch(() => [] as AppointmentReminder[]),
    ])
      .then(([appt, rems]) => {
        setAppointment(appt);
        setReminders(rems);
        // Walk-ins (slot_id=null) have no slot to fetch.
        if (appt.slot_id) {
          AppointmentAPI.getSlot(appt.slot_id).then(setSlot).catch(() => {});
        }
        // Fetch patient info for the detail view
        PatientAPI.getById(appt.patient_id).then((p: any) => {
          console.log("Patient data:", p);
          setPatient(p);
          setPatientContact(p?.contact ?? null);
        }).catch((e) => { console.warn("Patient fetch failed:", e); });
      })
      .catch(() => setError("Appointment not found."))
      .finally(() => setLoading(false));
  }, [id]);

  const doctorName = appointment
    ? doctors.find((d) => d.doctor_id === appointment.doctor_id)?.name
    : undefined;

  // Open the diagnosis pad for the checked-in patient, resuming the linked
  // visit if one already exists so we don't create a duplicate.
  const startVisit = () => {
    if (!appointment) return;
    const params = new URLSearchParams({
      patient_id: appointment.patient_id,
      doctor_id: appointment.doctor_id ?? "",
    });
    if (appointment.visit_id) params.set("visit_id", appointment.visit_id);
    navigate(`/90sdiagnosis?${params.toString()}`);
  };

  const handleComplete = async () => {
    if (!appointment || completing) return;
    setCompleting(true);
    try {
      const updated = await AppointmentAPI.complete(appointment.appointment_id);
      setAppointment(updated);
      toast.success("Appointment marked as complete");
    } catch (e: any) {
      toast.error(e?.message || "Failed to complete appointment");
    } finally {
      setCompleting(false);
    }
  };

  const handleApprove = async () => {
    if (!appointment || approving) return;
    setApproving(true);
    try {
      const updated = await AppointmentAPI.approve(appointment.appointment_id);
      setAppointment(updated);
      toast.success("Appointment approved — payment link sent to patient");
    } catch (e: any) {
      toast.error(e?.message || "Failed to approve appointment");
    } finally {
      setApproving(false);
    }
  };

  const handleResendPaymentLink = async () => {
    if (!appointment || approving) return;
    setApproving(true);
    try {
      // Re-call approve — SwasthiQ backend accepts status="approved" and
      // creates a new payment link if the old one expired, or resends the same one
      const updated = await AppointmentAPI.approve(appointment.appointment_id);
      setAppointment(updated);
      toast.success("Payment link resent to patient");
    } catch (e: any) {
      toast.error(e?.message || "Failed to resend payment link");
    } finally {
      setApproving(false);
    }
  };

  const handleDownload = async () => {
    if (!printRef.current || !appointment) return;
    setDownloading(true);
    try {
      // Make visible, capture, hide
      printRef.current.style.left = "0";
      printRef.current.style.top = "0";
      printRef.current.style.zIndex = "-1";
      await new Promise((r) => requestAnimationFrame(r));

      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        width: 794,
        windowWidth: 794,
      });

      printRef.current.style.left = "-9999px";
      printRef.current.style.zIndex = "";

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgH = (canvas.height * pageW) / canvas.width;
      // If content is taller than A4, scale it down to fit
      const finalH = Math.min(imgH, pageH);
      const finalW = imgH > pageH ? (pageW * pageH) / imgH : pageW;
      const xOffset = (pageW - finalW) / 2;
      pdf.addImage(imgData, "PNG", xOffset, 0, finalW, finalH);
      pdf.save(`appointment-${appointment.appointment_id.slice(0, 8).toUpperCase()}.pdf`);
    } catch {
      if (printRef.current) {
        printRef.current.style.left = "-9999px";
        printRef.current.style.zIndex = "";
      }
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-[#f7f8fc]">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0">
          <div className="text-slate-400 text-sm animate-pulse">Loading confirmation…</div>
        </div>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="flex h-screen bg-[#f7f8fc]">
        <Sidebar />
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-slate-400 lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0">
          <p className="text-sm">{error || "Appointment not found."}</p>
          <button onClick={() => navigate("/appointments")} className="text-sm text-cyan-600 hover:underline">
            ← Back to Appointments
          </button>
        </div>
      </div>
    );
  }

  const isConfirmed = ["confirmed", "in_progress", "completed"].includes(appointment.status);
  const isRequested = appointment.status === "requested";
  const isApproved = appointment.status === "approved";
  const isPublicBooking = appointment.booking_source === "public";
  const patientName = patient ? `${patient.first_name || ""} ${patient.last_name || ""}`.trim() : null;
  const patientPhone = patient?.contact_number || patientContact?.contact_number || null;
  const patientEmail = patientContact?.email || (appointment as any)?.patient_email || null;
  const displayReminders = reminders.length > 0
    ? reminders.map((r) => ({
        channel: r.channel === "whatsapp" ? "WhatsApp" : r.channel === "sms" ? "SMS" : "Email",
        status: r.status as ReminderStatus,
      }))
    : [
        { channel: "SMS", status: "pending" as ReminderStatus },
        { channel: "WhatsApp", status: "pending" as ReminderStatus },
        { channel: "Email", status: "pending" as ReminderStatus },
      ];

  return (
    <div className="flex min-h-screen bg-[#f7f8fc]">
      <Sidebar />

      <div className="flex-1 min-w-0 lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0">
        <AppHeader title="Appointment Confirmation" searchMode="both" />
        {/* ── Banner ───────────────────────────────────────────────── */}
        <div className={`px-8 py-6 flex items-center gap-4 ${
          isRequested ? "bg-gradient-to-r from-yellow-500 to-amber-500"
          : isApproved ? "bg-gradient-to-r from-orange-500 to-amber-500"
          : isConfirmed ? "bg-gradient-to-r from-green-500 to-emerald-500"
          : "bg-gradient-to-r from-cyan-500 to-blue-500"
        }`}>
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            {isRequested ? (
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : isConfirmed ? (
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            )}
          </div>
          <div>
            <h1 className="text-lg font-bold text-white leading-tight">
              {isRequested ? "Appointment Request" : isApproved ? "Approved — Awaiting Payment" : isConfirmed ? "Appointment Confirmed!" : "Appointment Booked"}
            </h1>
            <p className="text-white/80 text-sm mt-0.5">
              ID: {appointment.appointment_id.slice(0, 8).toUpperCase()}
              {isPublicBooking && " · Online Booking"}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {isRequested && canApproveReject && (
              <>
                <button onClick={handleApprove} disabled={approving}
                  className="px-4 py-2 rounded-lg bg-white text-green-700 text-sm font-semibold hover:bg-green-50 transition shadow-sm disabled:opacity-60">
                  {approving ? "Approving…" : "Approve"}
                </button>
                <button onClick={() => setShowReject(true)}
                  className="px-4 py-2 rounded-lg bg-white/20 text-white text-sm font-semibold hover:bg-white/30 transition">
                  Reject
                </button>
              </>
            )}
            {isApproved && canApproveReject && (
              <button onClick={handleResendPaymentLink} disabled={approving}
                className="px-4 py-2 rounded-lg bg-white text-orange-700 text-sm font-semibold hover:bg-orange-50 transition shadow-sm disabled:opacity-60">
                {approving ? "Sending…" : "Resend Payment Link"}
              </button>
            )}
            <AppointmentStatusBadge status={appointment.status} />
          </div>
        </div>

        {/* ── Cards ────────────────────────────────────────────────── */}
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-4xl">
          {/* Patient Information */}
          {patient && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4 lg:col-span-2">
              <h2 className="text-sm font-semibold text-slate-700 border-b border-slate-100 pb-2">
                Patient Information
                {isPublicBooking && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded bg-purple-50 text-purple-600 text-[10px] font-medium border border-purple-200">
                    Online Booking
                  </span>
                )}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-slate-400 mb-0.5">Name</p>
                  <p className="text-sm font-medium text-slate-800">{patientName || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-0.5">Phone</p>
                  <p className="text-sm font-medium text-slate-800">{patientPhone || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-0.5">Email</p>
                  <p className="text-sm font-medium text-slate-800">{patientEmail || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-0.5">Gender</p>
                  <p className="text-sm font-medium text-slate-800">{patient.gender || "—"}</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
            <h2 className="text-sm font-semibold text-slate-700 border-b border-slate-100 pb-2">Appointment Details</h2>
            <Row label="Doctor" value={doctorName ?? "—"} />
            <Row label="Date" value={slot ? fmtDate(slot.start_time) : fmtDate(appointment.created_at)} />
            {slot && (
              <Row label="Time" value={`${fmtTime(slot.start_time)} – ${fmtTime(slot.end_time)}`} />
            )}
            {appointment.arrived_at && <Row label="Arrived at" value={fmtTime(appointment.arrived_at)} />}
            {appointment.cancelled_at && <Row label="Cancelled at" value={fmtDate(appointment.cancelled_at)} />}
            {appointment.cancellation_reason && <Row label="Cancel reason" value={appointment.cancellation_reason} />}
            <Row label="Status"><AppointmentStatusBadge status={appointment.status} size="sm" /></Row>
            {appointment.notes && (
              <div className="pt-1">
                <p className="text-xs text-slate-400 mb-1">Notes</p>
                <p className="text-sm text-slate-700 bg-slate-50 rounded-xl px-3 py-2">{appointment.notes}</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
              <h2 className="text-sm font-semibold text-slate-700 border-b border-slate-100 pb-2">Reminders Sent</h2>
              <div className="space-y-3">
                {displayReminders.map(({ channel, status }) => (
                  <ReminderRow key={channel} channel={channel} status={status} />
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <h2 className="text-sm font-semibold text-slate-700 border-b border-slate-100 pb-2 mb-4">Next Steps</h2>
              <ul className="space-y-2.5">
                {[
                  "Patient will receive SMS and email reminders 24 hours before the appointment.",
                  "Receptionist should mark the patient as arrived when they check in.",
                  "Doctor can mark the appointment as complete after the consultation.",
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-xs text-slate-600">
                    <span className="w-4 h-4 rounded-full bg-cyan-100 text-cyan-600 font-semibold flex items-center justify-center flex-shrink-0 mt-0.5 text-[10px]">{i + 1}</span>
                    {step}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* ── Footer ───────────────────────────────────────────────── */}
        {/* Primary actions (status-gated) first, then navigation, then the exit
            action. Compact sizing keeps the whole row on a single line. */}
        <div className="px-6 pb-8 flex flex-wrap items-center gap-2 max-w-5xl">
          {appointment.status === "in_progress" && canStartVisit && (
            <button
              onClick={startVisit}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition shadow-sm"
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Start Visit
            </button>
          )}
          {appointment.status === "in_progress" && canComplete && (
            <button
              onClick={handleComplete}
              disabled={completing}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-green-500 text-white text-sm font-medium hover:bg-green-600 transition shadow-sm disabled:opacity-60"
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              {completing ? "Completing…" : "Mark as Complete"}
            </button>
          )}
          <button
            onClick={() => navigate(`/patients/${appointment.patient_id}`)}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition font-medium"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            View Patient Profile
          </button>
          <button onClick={() => navigate("/appointments/calendar")} className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition font-medium">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            View Calendar
          </button>
          <button onClick={handleDownload} disabled={downloading}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition font-medium disabled:opacity-60">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {downloading ? "Saving…" : "Download PDF"}
          </button>
          <button onClick={() => navigate("/appointments")} className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition shadow-sm">
            ← Back to Appointments
          </button>
        </div>
      </div>

      {/* ── Reject modal ─────────────────────────────────────────── */}
      <RejectAppointmentModal
        isOpen={showReject}
        appointmentId={appointment.appointment_id}
        onClose={() => setShowReject(false)}
        onSuccess={() => {
          setShowReject(false);
          // Refetch appointment to update status
          AppointmentAPI.get(appointment.appointment_id).then(setAppointment).catch(() => {});
        }}
      />

      {/* ── Hidden print template (captured by html2canvas) ──────── */}
      {appointment && (
        <div
          ref={printRef}
          style={{
            position: "fixed",
            left: "-9999px",
            top: "0",
            width: "794px",
            backgroundColor: "#ffffff",
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
            padding: "48px",
            boxSizing: "border-box",
          }}
        >
          {/* Header bar */}
          <div style={{
            background: isConfirmed ? "linear-gradient(to right, #22c55e, #10b981)" : "linear-gradient(to right, #06b6d4, #3b82f6)",
            borderRadius: "12px",
            padding: "24px 28px",
            marginBottom: "28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
            <div>
              <div style={{ color: "#ffffff", fontSize: "20px", fontWeight: "700", marginBottom: "4px" }}>
                {isConfirmed ? "Appointment Confirmed!" : "Appointment Booked"}
              </div>
              <div style={{ color: "rgba(255,255,255,0.85)", fontSize: "13px" }}>
                ID: {appointment.appointment_id.slice(0, 8).toUpperCase()}
              </div>
            </div>
            <div style={{
              backgroundColor: "rgba(255,255,255,0.25)",
              borderRadius: "20px",
              padding: "6px 14px",
              color: "#ffffff",
              fontSize: "12px",
              fontWeight: "600",
              textTransform: "capitalize",
            }}>
              {appointment.status.replace("_", " ")}
            </div>
          </div>

          {/* Two columns */}
          <div style={{ display: "flex", gap: "20px" }}>
            {/* Left: Appointment Details */}
            <div style={{
              flex: 1,
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "24px",
            }}>
              <div style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", borderBottom: "1px solid #f1f5f9", paddingBottom: "10px", marginBottom: "16px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Appointment Details
              </div>
              {[
                { label: "Doctor", value: doctorName ?? "—" },
                { label: "Date", value: slot ? fmtDate(slot.start_time) : fmtDate(appointment.created_at) },
                ...(slot ? [{ label: "Time", value: `${fmtTime(slot.start_time)} – ${fmtTime(slot.end_time)}` }] : []),
                ...(appointment.arrived_at ? [{ label: "Arrived at", value: fmtTime(appointment.arrived_at) }] : []),
                ...(appointment.cancelled_at ? [{ label: "Cancelled at", value: fmtDate(appointment.cancelled_at) }] : []),
                ...(appointment.cancellation_reason ? [{ label: "Cancel reason", value: appointment.cancellation_reason }] : []),
                { label: "Status", value: appointment.status.replace("_", " ") },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                  <span style={{ fontSize: "12px", color: "#94a3b8" }}>{label}</span>
                  <span style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b", textAlign: "right", maxWidth: "60%" }}>{value}</span>
                </div>
              ))}
              {appointment.notes && (
                <div style={{ marginTop: "12px", backgroundColor: "#f8fafc", borderRadius: "8px", padding: "10px 12px" }}>
                  <div style={{ fontSize: "11px", color: "#94a3b8", marginBottom: "4px" }}>Notes</div>
                  <div style={{ fontSize: "13px", color: "#334155" }}>{appointment.notes}</div>
                </div>
              )}
            </div>

            {/* Right: Reminders + Next Steps */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "16px" }}>
              {/* Reminders */}
              <div style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "24px" }}>
                <div style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", borderBottom: "1px solid #f1f5f9", paddingBottom: "10px", marginBottom: "16px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Reminders Sent
                </div>
                {displayReminders.map(({ channel, status }) => (
                  <div key={channel} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <div style={{
                        width: "28px", height: "28px", borderRadius: "8px", backgroundColor: "#f1f5f9",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "11px", fontWeight: "700", color: "#64748b",
                      }}>
                        {channel[0]}
                      </div>
                      <span style={{ fontSize: "13px", color: "#334155" }}>{channel}</span>
                    </div>
                    <div style={{
                      display: "flex", alignItems: "center", gap: "5px",
                      border: "1px solid #e2e8f0", borderRadius: "20px",
                      padding: "4px 10px", fontSize: "11px", fontWeight: "600",
                      color: STATUS_COLORS[status],
                    }}>
                      <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: STATUS_COLORS[status] }} />
                      {REMINDER_CONFIG[status].label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Next Steps */}
              <div style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "24px" }}>
                <div style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", borderBottom: "1px solid #f1f5f9", paddingBottom: "10px", marginBottom: "16px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Next Steps
                </div>
                {[
                  "Patient will receive SMS and email reminders 24 hours before the appointment.",
                  "Receptionist should mark the patient as arrived when they check in.",
                  "Doctor can mark the appointment as complete after the consultation.",
                ].map((step, i) => (
                  <div key={i} style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
                    <div style={{
                      width: "18px", height: "18px", borderRadius: "50%", backgroundColor: "#e0f2fe",
                      color: "#0e7490", fontSize: "10px", fontWeight: "700", flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center", marginTop: "1px",
                    }}>
                      {i + 1}
                    </div>
                    <span style={{ fontSize: "12px", color: "#475569", lineHeight: "1.5" }}>{step}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ marginTop: "28px", paddingTop: "16px", borderTop: "1px solid #f1f5f9", textAlign: "center", fontSize: "11px", color: "#94a3b8" }}>
            Generated on {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────
const Row: React.FC<{ label: string; value?: string; children?: React.ReactNode }> = ({ label, value, children }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="text-xs text-slate-400 flex-shrink-0">{label}</span>
    {children ?? <span className="text-sm font-medium text-slate-800 text-right">{value}</span>}
  </div>
);

const ReminderRow: React.FC<{ channel: string; status: ReminderStatus }> = ({ channel, status }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2.5">
      <span className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 text-xs font-bold">
        {channel[0]}
      </span>
      <span className="text-sm text-slate-700">{channel}</span>
    </div>
    <ReminderBadge status={status} />
  </div>
);

export default AppointmentConfirmationPage;
